import React, { useCallback, useEffect, useRef } from "react";
import { Box, CardActions, CardContent, Typography, useTheme } from "@material-ui/core";
import { MaterialDetailsViewModel } from "api/factories/creatives.factory";
import {
    CardMediaPlaceholder,
    ConfirmationDialog,
    Dropzone,
    DropzoneBox,
    MaterialActionArea,
    MaterialCardContent,
    Loader,
} from "components";
import { useMaterialDetails, useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { serverAddress } from "utils";
import { CreativeToolbar, CreativeCard } from "pages/MaterialSpecs/components/CreativesSection";
import { useConfirmationDialog, usePermissions, usePreviousProp } from "hooks";
import { useSelector } from "react-redux";
import { getMIMETypes, getOptionalUploadStatusById } from "store/features/materialSpecs/specs";
import { CardHeader, CardSubheader } from "components/MaterialCard";
import { useTranslation } from "react-i18next";

interface CreativeProps {
    selected: boolean;
    printed: boolean;
    expectedSize?: string;
    materialSpecId: number;
    dimensions: ClientRect | null;
    materialDetails: MaterialDetailsViewModel;
}

const printMIMETypes = ["application/pdf", "application/postscript"];

export const Creative = ({
    expectedSize,
    selected,
    printed,
    materialSpecId,
    materialDetails,
    dimensions,
}: CreativeProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const fileRef = useRef<File[] | null>();
    const { select, dropExistingMaterials, selectLast } = useMaterialSpecs();
    const { isMaterialEmpty, thumbnailUrl } = useMaterialDetails(materialDetails);
    const MIMETypesConfig = useSelector(getMIMETypes);
    const isUploadingOptional = useSelector(getOptionalUploadStatusById(materialSpecId));
    const prevUploadStatus = usePreviousProp(materialDetails.isUploading);
    const prevOptionalUploadStatus = usePreviousProp(isUploadingOptional);
    const { UPLOAD_ADMATERIAL } = usePermissions(materialDetails.permissions);
    const { isConfirmationOpen, handleOnOpen, handleOnExited, handleOnConfirm, handleOnClose } = useConfirmationDialog(
        () => {
            // important, ref must contain file ref before sending in to BE, nice to have it covered in tests!
            if (fileRef.current) {
                dropExistingMaterials(materialSpecId, materialDetails.id)(fileRef.current);
            }

            handleOnClose();
            // clear ref
            fileRef.current = null;
        }
    );

    // auto-select uploaded material
    useEffect(() => {
        /* Keep in mind, that print materials have negative IDs, which are updated only after uploading material
        so after uploading material, if you will look for "isUploading" flag you will receive "undefined" because old
        ID will not exist anymore */

        if (
            (prevUploadStatus && !materialDetails.isUploading) ||
            (printed && prevUploadStatus === undefined && !materialDetails.isUploading)
        ) {
            select(printed, materialSpecId, materialDetails.id);
        }
    }, [prevUploadStatus, materialDetails.isUploading, printed, materialSpecId, materialDetails.id, select]);

    // auto-select uploaded optional material
    useEffect(() => {
        if (prevOptionalUploadStatus && !isUploadingOptional) {
            selectLast(materialSpecId);
        }
    }, [prevOptionalUploadStatus, isUploadingOptional, materialSpecId, selectLast]);

    const handleReplaceMaterial = useCallback(
        (file: File[]) => {
            // store temporarily in ref!
            fileRef.current = file;
            handleOnOpen(materialDetails.id)();
        },
        [handleOnOpen, materialDetails.id]
    );

    const assignTitle = () =>
        materialDetails.isDeleting
            ? ""
            : materialDetails.status.code === 3
            ? t("campaign.label.processingCreative")
            : t("campaign.label.uploadingCreative");

    return (
        <>
            {/*TODO: Create confirmation dialog provider to prevent duplication of this component*/}
            <ConfirmationDialog isOpen={isConfirmationOpen} onClose={handleOnConfirm} onExited={handleOnExited}>
                <Typography variant="body2">
                    {t("campaign.warnings.deleteMaterialWarning", { id: materialDetails.id })}
                </Typography>
            </ConfirmationDialog>
            <CreativeCard selected={selected}>
                {dimensions && (
                    <>
                        <CardContent
                            style={{ padding: `${theme.spacing(1)}px` }}
                            onClick={() => select(printed, materialSpecId, materialDetails.id)}>
                            <CardHeader
                                label={materialDetails.externalId || materialDetails.inputFileName}
                                status={materialDetails.status.text}
                                color={materialDetails.status.color}
                            />
                            <CardSubheader
                                label={materialDetails.bookedSize || expectedSize || ""}
                                format={materialDetails.format}
                            />
                        </CardContent>
                        {isMaterialEmpty ? (
                            <MaterialCardContent>
                                <DropzoneBox display="flex" alignItems="center" justifyContent="center">
                                    {materialDetails.isUploading || materialDetails.isDeleting ? (
                                        <Loader title={assignTitle()} />
                                    ) : (
                                        <Dropzone
                                            multiple={!printed}
                                            label={t("campaign.label.dragAndDropArea")}
                                            allowed={UPLOAD_ADMATERIAL?.display}
                                            disabled={UPLOAD_ADMATERIAL?.disabled}
                                            onDrop={dropExistingMaterials(materialSpecId, materialDetails.id)}
                                            mimeTypes={printed ? printMIMETypes : MIMETypesConfig}
                                        />
                                    )}
                                </DropzoneBox>
                            </MaterialCardContent>
                        ) : (
                            <MaterialActionArea
                                disableRipple
                                onClick={() => select(printed, materialSpecId, materialDetails.id)}>
                                {materialDetails.isUploading || materialDetails.isDeleting ? (
                                    <DropzoneBox
                                        sizeY={dimensions.width}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center">
                                        <Loader title={assignTitle()} />
                                    </DropzoneBox>
                                ) : (
                                    <Box width="100%" height="100%" p={0}>
                                        <Dropzone
                                            noClick
                                            multiple={!printed}
                                            disabled={UPLOAD_ADMATERIAL?.disabled}
                                            allowed={UPLOAD_ADMATERIAL?.display}
                                            onDrop={handleReplaceMaterial}
                                            mimeTypes={printed ? printMIMETypes : MIMETypesConfig}>
                                            <CardMediaPlaceholder
                                                width={dimensions.width}
                                                url={`${serverAddress}${thumbnailUrl}`}
                                            />
                                        </Dropzone>
                                    </Box>
                                )}
                            </MaterialActionArea>
                        )}
                        <CardActions>
                            <CreativeToolbar
                                permissions={materialDetails.permissions}
                                printed={printed}
                                materialSpecId={materialSpecId}
                                materialId={materialDetails.id}
                                isThirdPartyTagMaterial={materialDetails.isThirdPartyTagMaterial}
                                isMaterialEmpty={isMaterialEmpty}
                            />
                        </CardActions>
                    </>
                )}
            </CreativeCard>
        </>
    );
};
